import * as React from 'react'
import { useMemo } from 'react'

import { Button } from '../../../../app/features/common/Button'
import { Card } from '../../../../app/features/common/Card'
import { Toggle } from '../../../../app/features/common/Toggle'
import { ChartIcon } from '../../../../app/features/icons/Chart'
import { ArrowRightIcon } from '../../../../app/features/icons/ArrowRight'
import { DriversData } from './DriversData'
import { DriverCell } from '../../components/DriverCell'

import { Driver, SelectedDriver } from '@/app/types/creator'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { FaIcon, faArrowRightFromLineSolid } from '@/lib/fa'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  cardReadStatusClass,
  getCardReadStatus,
} from '../../utils/cardReadStatus'

type Props = {
  drivers: Driver[]
  onNext: () => void
  onPrev?: () => void
  isLoading: boolean
  selectedDrivers: SelectedDriver[]
  setSelectedDrivers: React.Dispatch<React.SetStateAction<SelectedDriver[]>>
  periodStart?: string | null
}

export const DriversStep = ({
  drivers,
  onNext,
  onPrev,
  isLoading = false,
  selectedDrivers,
  setSelectedDrivers,
  periodStart = null,
}: Props) => {
  const { tSafe } = useLocalize()
  const columns = React.useMemo<TableHeaderColumn[]>(
    () => [
      {
        title: tSafe('creator.drivers.column.driver', 'Kierowca'),
        key: 'driver',
        type: 'string',
        sort: true,
        secondaryKey: 'surname',
      },
      {
        title: tSafe('creator.drivers.column.workingDays', 'Dni pracujące'),
        key: 'workingDays',
        type: 'number',
        sort: true,
      },
      {
        title: tSafe(
          'creator.drivers.column.nonWorkingDays',
          'Dni bez pracy',
        ),
        key: 'nonWorkingDays',
        type: 'number',
        sort: true,
      },
      {
        title: tSafe(
          'creator.drivers.column.lastCardReadDate',
          'Data odczytu karty',
        ),
        key: 'lastCardReadDate',
        type: 'date',
        sort: true,
      },
      {
        title: tSafe(
          'creator.drivers.column.addToCalculation',
          'Dodaj do rozliczenia',
        ),
        key: 'addToCalculation',
        type: 'string',
        sort: false,
      },
      {
        title: ' ',
        key: 'chart',
        type: 'string',
        sort: false,
      },
    ],
    [tSafe],
  )
  const { sortedData, sortConfig, handleHeaderClick } = useSortableData<Driver>(
    {
      data: drivers,
      tableColumns: columns,
      includeActiveSorting: false,
    },
  )

  const unknownDrivers = useMemo(
    () => sortedData.filter((d) => d.status === 'unknown'),
    [sortedData],
  )

  const driversWithoutContract = useMemo(
    () =>
      sortedData.filter(
        (d) => d.status !== 'unknown' && d.hasContractInPeriod === false,
      ),
    [sortedData],
  )
  const driversWithWorkingDays = useMemo(
    () =>
      sortedData.filter(
        (d) =>
          d.status !== 'unknown' &&
          d.hasContractInPeriod !== false &&
          d.workingDays > 0,
      ),
    [sortedData],
  )
  const driversWithoutWorkingDays = useMemo(
    () =>
      sortedData.filter(
        (d) =>
          d.status !== 'unknown' &&
          d.hasContractInPeriod !== false &&
          d.workingDays === 0,
      ),
    [sortedData],
  )

  const renderCardReadDate = (lastCardReadDate: string | null) => {
    const status = getCardReadStatus(lastCardReadDate, periodStart)
    const text = lastCardReadDate ?? '-'
    return (
      <div className="tw-flex tw-items-center tw-py-3.5">
        <span className={cardReadStatusClass(status)}>{text}</span>
      </div>
    )
  }

  const toggleSelectedDriver = (id: string) => {
    const driver = drivers.find((d) => d.id === id)
    if (!driver) return

    const selectedDriver: SelectedDriver = {
      driverId: driver.id,
      name: driver.driver,
      surname: driver.surname || '',
    }

    setSelectedDrivers((prev) => {
      const isSelected = prev.some((d) => d.driverId === id)
      return isSelected
        ? prev.filter((d) => d.driverId !== id)
        : [...prev, selectedDriver]
    })
  }

  const toggleSelectAllInList = (list: Driver[], shouldSelectAll: boolean) => {
    if (!list.length) return
    if (shouldSelectAll) {
      setSelectedDrivers((prev) => {
        const existingIds = new Set(prev.map((d) => d.driverId))
        const toAdd: SelectedDriver[] = list
          .filter((d) => !existingIds.has(d.id))
          .map((d) => ({
            driverId: d.id,
            name: d.driver,
            surname: d.surname || '',
          }))
        return toAdd.length ? [...prev, ...toAdd] : prev
      })
    } else {
      const listIds = new Set(list.map((d) => d.id))
      setSelectedDrivers((prev) =>
        prev.filter((d) => !listIds.has(d.driverId)),
      )
    }
  }

  const isListAllSelected = (list: Driver[]) =>
    list.length > 0 &&
    list.every((d) => selectedDrivers.some((sd) => sd.driverId === d.id))

  const eligibleUnknownDrivers = useMemo(
    () => unknownDrivers.filter((d) => d.hasAllPersonalData),
    [unknownDrivers],
  )

  return (
    <Card
      className="tw-flex tw-min-h-0 tw-max-w-full tw-flex-1 tw-flex-col tw-bg-base-100 tw-pb-3"
      classNameContent="!tw-flex !tw-px-0 !tw-pt-0 tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"
      paddingTop={false}
      noContentPadding
    >
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-rounded-t-lg">
        {isLoading ? (
          <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        ) : drivers.length === 0 ? (
          <div className="tw-px-4 tw-py-8 tw-text-center tw-text-base-content/70">
            {tSafe(
              'creator.drivers.empty',
              'Brak kierowców w firmie. Dodaj kierowców, aby móc rozpocząć rozliczenie.',
            )}
          </div>
        ) : (
          <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-rounded-t-lg">
            {!!driversWithWorkingDays.length && (
              <DriversData
                isFirst
                title={tSafe(
                  'creator.drivers.group.active',
                  'czynnie aktywnych kierowców',
                )}
                columns={columns}
                drivers={driversWithWorkingDays}
                sortConfig={sortConfig}
                onHeaderClick={handleHeaderClick}
                selectAllChecked={isListAllSelected(driversWithWorkingDays)}
                onToggleSelectAll={(checked) =>
                  toggleSelectAllInList(driversWithWorkingDays, checked)
                }
                renderCell={(row, col) => {
                  if (col.key === 'addToCalculation')
                    return (
                      <div className="tw-flex tw-items-center">
                        <Toggle
                          onChange={() => toggleSelectedDriver(row.id)}
                          checked={selectedDrivers.some(
                            (d) => d.driverId === row.id,
                          )}
                        />
                      </div>
                    )
                  if (col.key === 'driver')
                    return (
                      <DriverCell
                        name={`${row.driver} ${row.surname || ''}`.trim()}
                      />
                    )
                  if (col.key === 'chart')
                    return (
                      <button className="tw-rounded-lg tw-p-1 tw-text-focus-300 tw-transition-colors hover:tw-bg-base-200">
                        <ChartIcon className="tw-h-4 tw-w-4" />
                      </button>
                    )
                  if (col.key === 'lastCardReadDate')
                    return renderCardReadDate(row.lastCardReadDate)
                  return (
                    <div className="tw-flex tw-items-center tw-py-3.5">
                      {String(
                        (row as unknown as Record<string, unknown>)[
                          col.key as string
                        ] ?? '',
                      )}
                    </div>
                  )
                }}
              />
            )}

            {!!driversWithoutWorkingDays.length && (
              <DriversData
                isFirst={driversWithWorkingDays.length === 0}
                title={tSafe(
                  'creator.drivers.group.activeNoHours',
                  'kierowców aktywnych przez 0 godzin',
                )}
                columns={columns}
                drivers={driversWithoutWorkingDays}
                sortConfig={sortConfig}
                onHeaderClick={handleHeaderClick}
                selectAllDisabled
                onToggleSelectAll={() => {}}
                renderCell={(row, col) => {
                  if (col.key === 'driver')
                    return (
                      <DriverCell
                        name={`${row.driver} ${row.surname || ''}`.trim()}
                      />
                    )
                  if (col.key === 'addToCalculation')
                    return (
                      <div className="tw-flex tw-items-center">
                        <Toggle disabled />
                      </div>
                    )
                  if (col.key === 'lastCardReadDate')
                    return renderCardReadDate(row.lastCardReadDate)
                  return (
                    <div className="tw-flex tw-items-center tw-py-3.5">
                      {String(
                        (row as unknown as Record<string, unknown>)[
                          col.key as string
                        ] ?? '-',
                      )}
                    </div>
                  )
                }}
              />
            )}
            {!!driversWithoutContract.length && (
              <DriversData
                isFirst={
                  driversWithWorkingDays.length === 0 &&
                  driversWithoutWorkingDays.length === 0
                }
                title={tSafe(
                  'creator.drivers.group.noContract',
                  'kierowców bez umowy w okresie',
                )}
                columns={columns}
                drivers={driversWithoutContract}
                sortConfig={sortConfig}
                onHeaderClick={handleHeaderClick}
                selectAllDisabled
                onToggleSelectAll={() => {}}
                renderCell={(row, col) => {
                  if (col.key === 'driver')
                    return (
                      <DriverCell
                        name={`${row.driver} ${row.surname || ''}`.trim()}
                      />
                    )
                  if (col.key === 'addToCalculation')
                    return (
                      <div className="tw-flex tw-items-center">
                        <Toggle disabled />
                      </div>
                    )
                  if (col.key === 'lastCardReadDate')
                    return renderCardReadDate(row.lastCardReadDate)
                  return (
                    <div className="tw-flex tw-items-center tw-py-3.5">
                      {String(
                        (row as unknown as Record<string, unknown>)[
                          col.key as string
                        ] ?? '-',
                      )}
                    </div>
                  )
                }}
              />
            )}
            {!!unknownDrivers.length && (
              <DriversData
                isFirst={
                  driversWithWorkingDays.length === 0 &&
                  driversWithoutWorkingDays.length === 0 &&
                  driversWithoutContract.length === 0
                }
                title={tSafe(
                  'creator.drivers.group.unknown',
                  'nieznanych kierowców',
                )}
                columns={columns}
                drivers={unknownDrivers}
                sortConfig={sortConfig}
                onHeaderClick={handleHeaderClick}
                selectAllChecked={
                  eligibleUnknownDrivers.length > 0 &&
                  isListAllSelected(eligibleUnknownDrivers)
                }
                selectAllDisabled={eligibleUnknownDrivers.length === 0}
                onToggleSelectAll={(checked) =>
                  toggleSelectAllInList(eligibleUnknownDrivers, checked)
                }
                renderCell={(row, col) => {
                  if (col.key === 'driver')
                    return (
                      <DriverCell
                        name={`${row.driver} ${row.surname || ''}`.trim()}
                      />
                    )
                  if (col.key === 'addToCalculation') {
                    return row.hasAllPersonalData ? (
                      <Toggle
                        onChange={() => toggleSelectedDriver(row.id)}
                        checked={selectedDrivers.some(
                          (d) => d.driverId === row.id,
                        )}
                      />
                    ) : (
                      <Button size="xs" variant="link">
                        <span>
                          {tSafe(
                            'creator.drivers.provideData',
                            'Podaj dane kierowcy',
                          )}
                        </span>
                        <span className="tw-h-5 tw-w-5 tw-text-left">
                          <ArrowRightIcon />
                        </span>
                      </Button>
                    )
                  }
                  if (col.key === 'lastCardReadDate')
                    return renderCardReadDate(row.lastCardReadDate)
                  return (
                    <div className="tw-flex tw-items-center tw-py-3.5">
                      {String(
                        (row as unknown as Record<string, unknown>)[
                          col.key as string
                        ] ?? '-',
                      )}
                    </div>
                  )
                }}
              />
            )}
          </div>
        )}
      </div>

      <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-base-300 tw-px-4 tw-pt-3 sm:tw-flex-row">
        {onPrev && (
          <Button
            size="sm"
            onClick={onPrev}
            className="tw-h-10 tw-rounded-lg tw-font-medium"
          >
            <span>
              <FaIcon
                icon={faArrowRightFromLineSolid}
                className="tw-rotate-180"
              />
            </span>
            <span>{tSafe('creator.footer.prev', 'Poprzedni krok')}</span>
          </Button>
        )}
        <div className="tw-flex tw-gap-1 tw-text-sm">
          <span className="tw-font-semibold">{selectedDrivers.length}</span>
          <span>
            {tSafe(
              'creator.drivers.totalSelected',
              'kierowców łącznie oznaczonych do rozliczenia',
            )}
          </span>
        </div>
        <Button
          size="sm"
          onClick={onNext}
          text={tSafe(
            'creator.drivers.startSettlement',
            'Rozpocznij rozliczenie',
          )}
          className="tw-h-10 tw-rounded-lg tw-font-medium"
          disabled={!selectedDrivers.length}
        />
      </div>
    </Card>
  )
}
